import React, { useState, useRef, useLayoutEffect } from 'react';
import { FaSort, FaSortUp, FaSortDown, FaEllipsisH, FaChevronUp } from 'react-icons/fa';
import Loader from '../../../../common/Loader';

// Componente per celle con testo troncato a 2 righe
const TruncatedCell = ({ content, maxLines = 2 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const textRef = useRef(null);

  const isReactElement = React.isValidElement(content);
  const textContent = isReactElement ? '' : String(content || '');
  const isShortText = textContent.length < 60 && !textContent.includes('\n');

  // Misura reale dopo il render
  useLayoutEffect(() => {
    if (textRef.current && !isReactElement && !isShortText) {
      const element = textRef.current;
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 20;
      const maxHeight = lineHeight * maxLines;
      setNeedsTruncation(element.scrollHeight > maxHeight + 2);
    }
  }, [textContent, maxLines, isReactElement, isShortText]);

  // Se è un elemento React (come un link), renderizzalo direttamente
  if (isReactElement) {
    return content;
  }

  // Testo molto corto - nessun troncamento necessario
  if (isShortText) {
    return <span className="whitespace-pre-wrap">{textContent}</span>;
  }

  return (
    <div className="relative">
      {/* Contenuto visibile */}
      <div 
        ref={textRef}
        className="whitespace-pre-wrap break-words"
        style={!isExpanded ? {
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        } : {}}
      >
        {textContent}
      </div>
      
      {/* Pulsante espandi/comprimi - solo se serve */}
      {needsTruncation && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="mt-0.5 inline-flex items-center justify-center w-5 h-4 rounded bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-600 dark:text-gray-300 transition-colors duration-150 focus:outline-none"
          title={isExpanded ? 'Comprimi' : 'Espandi'}
        >
          {isExpanded ? (
            <FaChevronUp className="w-2 h-2" />
          ) : (
            <FaEllipsisH className="w-2.5 h-2.5" />
          )}
        </button>
      )}
    </div>
  );
};

// Helper function to check if a string is a valid URL
const isValidUrl = (string) => {
  if (typeof string !== 'string' || !string.trim()) return false;
  
  const trimmed = string.trim();
  
  // Check if starts with common URL patterns
  if (trimmed.startsWith('http://') || 
      trimmed.startsWith('https://') || 
      trimmed.startsWith('www.')) {
    return true;
  }
  
  // Try to parse as URL
  try {
    const url = new URL(trimmed.startsWith('www.') ? `https://${trimmed}` : trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

// Helper function to strip HTML tags and get plain text
const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return html;
  // Check if string contains HTML tags
  if (/<[^>]+>/.test(html)) {
    // Create a temporary element to extract text content
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  return html;
};

// Helper function to render cell value (with URL detection and HTML stripping)
const renderCellValue = (value) => {
  if (value === null || value === undefined) return '';
  
  let stringValue = String(value);
  
  // Strip HTML tags for display in table
  stringValue = stripHtmlTags(stringValue);
  
  // Check if the value is a URL
  if (isValidUrl(stringValue)) {
    console.log('[DataTable] Rendering URL:', stringValue);
    // Ensure URL has protocol for href
    const href = stringValue.startsWith('www.') 
      ? `https://${stringValue}` 
      : stringValue;
    
    return (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
        onClick={(e) => e.stopPropagation()} // Prevent row selection when clicking link
      >
        {stringValue}
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </a>
    );
  }
  
  return stringValue;
};

// Updated function to handle different status fields and their values with VIVID colors matching filters
const getStatusBasedRowClasses = (statusValue, statusField = 'MAC_Stato') => {
    // Handle MAC_Stato (existing MacroFunzioni logic) - AGGIORNATO con colori più vivaci
    if (statusField === 'MAC_Stato') {
        switch (statusValue) {
            case 'I': // Inserito - Verde VIVACE (identico ai filtri)
                return 'bg-green-100 dark:bg-green-800/40 text-green-900 dark:text-green-100 font-medium';
            case 'C': // Cri - Rosa VIVACE (identico ai filtri)
                return 'bg-pink-100 dark:bg-pink-800/40 text-pink-900 dark:text-pink-100 font-medium';
            case 'F': // Filippo - Giallo VIVACE (identico ai filtri)
                return 'bg-yellow-100 dark:bg-yellow-800/40 text-yellow-900 dark:text-yellow-100 font-medium';
            case 'M': // Marco - Blu VIVACE (identico ai filtri)
                return 'bg-blue-100 dark:bg-blue-800/40 text-blue-900 dark:text-blue-100 font-medium';
            case 'P': // Paul - VIOLA VIVACE (NUOVO COLORE per distinguere da Filippo)
                return 'bg-purple-100 dark:bg-purple-800/40 text-purple-900 dark:text-purple-100 font-medium';
            case 'T': // Terminato - Grigio chiaro (neutro ma definito)
                return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-medium';
            case 'D': // Da rilasciare - Ciano VIVACE (identico ai filtri)
                return 'bg-cyan-100 dark:bg-cyan-800/40 text-cyan-900 dark:text-cyan-100 font-medium';
            case 'S': // Standby - Arancione VIVACE (identico ai filtri)
                return 'bg-orange-100 dark:bg-orange-800/40 text-orange-900 dark:text-orange-100 font-medium';
            case 'U': // Urgente - Rosso VIVACE (identico ai filtri)
                return 'bg-red-100 dark:bg-red-800/40 text-red-900 dark:text-red-100 font-medium';
            default:
                return 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100';
        }
    }
    
    // Handle RIC_Id_TipiUrgenza (new Richieste logic)
    if (statusField === 'RIC_Id_TipiUrgenza') {
        switch (statusValue) {
            case 1: // Tipo urgenza 1 - Invariato (colore normale)
                return 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100';
            case 2: // Tipo urgenza 2 - Arancione più definito
                return 'bg-orange-100 dark:bg-orange-800/40 text-orange-900 dark:text-orange-100 font-medium';
            case 3: // Tipo urgenza 3 - Rosso
                return 'bg-red-100 dark:bg-red-800/40 text-red-900 dark:text-red-100 font-medium';
            default:
                return 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100';
        }
    }
    
    // Default case (no coloring)
    return 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100';
};

export const DataTable = ({
  table,
  loading = false,
  onRowSelect,
  onRowDoubleClick, // NUOVO: Per aprire il form di modifica con doppio click
  primaryKey = 'id',
  selectedRow,
  theme = { themeColor: 'primary' },
  statusField = null,
  lookupOptions = {}, // AGGIUNTO: Ricevi le lookup options
}) => {
  const isOverallLoading = loading;
  const rows = table.getRowModel().rows;
  const colSpan = table.getHeaderGroups()[0]?.headers.length || 1;
  const hasNoData = !isOverallLoading && rows.length === 0;

  // CORRETTO: Handle row click for row selection with position calculation
  const handleRowClickInternal = (event, row) => {
    if (onRowSelect) {
      // MODIFICATO: Usa la posizione del mouse invece che quella della riga
      const position = {
        mouseX: event.clientX, // Posizione X del mouse
        mouseY: event.clientY, // Posizione Y del mouse
        left: event.clientX,   // Per compatibilità con il codice esistente
        viewportTop: event.clientY, // Per compatibilità con il codice esistente
      };


      onRowSelect(row.original, position);
    }
  };

  // NUOVO: Handle right-click (context menu) for RowActionPopup
  const handleRowContextMenu = (event, row) => {
    event.preventDefault(); // Previeni il menu contestuale del browser
    event.stopPropagation(); // Previeni propagazione per evitare conflitti
    console.log('[DataTable] Context menu triggered on row:', row.original);
    if (onRowSelect) {
      const position = {
        mouseX: event.clientX,
        mouseY: event.clientY,
        left: event.clientX,
        viewportTop: event.clientY,
      };
      console.log('[DataTable] Calling onRowSelect with position:', position);
      onRowSelect(row.original, position);
    }
  };

  // NUOVO: Handle double-click per aprire il form di modifica
  const handleRowDoubleClickInternal = (event, row) => {
    if (onRowDoubleClick) {
      onRowDoubleClick(row.original);
    }
  };

  // AGGIUNTO: Funzione helper per ottenere MAC_Stato dal lookup
  const getMacStatoFromLookup = (record) => {
    if (!lookupOptions.macrofunzionilookup || !record.TEM_Id_MacroFunzioni) {
      return null;
    }
    
    const macroFunzione = lookupOptions.macrofunzionilookup.find(
      macro => macro.value === record.TEM_Id_MacroFunzioni
    );
    
    // CORRETTO: Il MAC_Stato è dentro la proprietà 'original' dell'oggetto lookup
    const macStato = macroFunzione?.original?.MAC_Stato || macroFunzione?.MAC_Stato;
    
    return macStato || null;
  };

  return (
    <div className="overflow-x-auto bg-white dark:bg-gray-900 rounded-lg shadow">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-r border-gray-200 dark:border-gray-700 last:border-r-0"
                  style={header.column.columnDef.meta?.maxWidth ? { maxWidth: header.column.columnDef.meta.maxWidth, overflow: 'hidden' } : undefined}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div className="flex items-center space-x-1">
                    <span>{header.column.columnDef.header}</span>
                    {header.column.getCanSort() && (
                      <span className="ml-2">
                        {header.column.getIsSorted() === 'asc' ? (
                          <FaSortUp className="w-3 h-3" />
                        ) : header.column.getIsSorted() === 'desc' ? (
                          <FaSortDown className="w-3 h-3" />
                        ) : (
                          <FaSort className="w-3 h-3" />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {isOverallLoading ? (
            <tr>
              <td colSpan={colSpan} className="text-center py-8">
                <Loader />
              </td>
            </tr>
          ) : hasNoData ? (
            <tr>
              <td colSpan={colSpan} className="text-center py-8">
                <div className="text-gray-500 dark:text-gray-400">
                  <p className="text-lg font-semibold">Nessun dato disponibile</p>
                  <p className="text-sm">Non sono presenti record da visualizzare</p>
                </div>
              </td>
            </tr>
          ) : (
            rows.map(row => {
              const isSelected = selectedRow && row.original[primaryKey] === selectedRow[primaryKey];
              
              // AGGIORNATO: Get status classes with lookup support per TestModifiche
              let statusClasses;
              let hasStatusColor = false;
              
              if (statusField) {
                let statusValue = row.original[statusField];
                
                // AGGIUNTO: Per TestModifiche, se statusField è MAC_Stato ma non esiste nel record,
                // prendi il MAC_Stato dalla lookup macrofunzionilookup
                if (statusField === 'MAC_Stato' && !statusValue && row.original.TEM_Id_MacroFunzioni) {
                  statusValue = getMacStatoFromLookup(row.original);
                }
                
                statusClasses = getStatusBasedRowClasses(statusValue, statusField);
                hasStatusColor = statusValue && statusValue !== null;
              } else if (row.original.MAC_Stato !== undefined) {
                // Backward compatibility: if MAC_Stato exists, use it
                statusClasses = getStatusBasedRowClasses(row.original.MAC_Stato, 'MAC_Stato');
                hasStatusColor = row.original.MAC_Stato && row.original.MAC_Stato !== null;
              } else {
                // No status coloring - CORRETTO: testo bianco per tema scuro
                statusClasses = 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100';
              }

              return (
                <tr
                  key={row.id}
                  className={`
                    ${statusClasses}
                    ${isSelected ? 'ring-2 ring-blue-500' : ''}
                    ${hasStatusColor 
                      ? 'hover:shadow-lg hover:shadow-gray-400/50 dark:hover:shadow-gray-900/50' 
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                    }
                    cursor-pointer transition-all duration-200
                  `}
                  onContextMenu={(e) => handleRowContextMenu(e, row)}
                  onDoubleClick={(e) => handleRowDoubleClickInternal(e, row)}
                >
                  {row.getVisibleCells().map(cell => {
                    const cellValue = cell.getValue();
                    let cellContent = null;
                    let shouldTruncate = true; // Di default trunca
                    
                    if (cellValue !== null && cellValue !== undefined) {
                      let stringValue = String(cellValue);
                      
                      // Strip HTML tags for display
                      stringValue = stripHtmlTags(stringValue);
                      
                      // Prima controlla se è un URL (priorità assoluta)
                      if (isValidUrl(stringValue)) {
                        shouldTruncate = false; // Non troncare gli URL
                        const href = stringValue.startsWith('www.') 
                          ? `https://${stringValue}` 
                          : stringValue;
                        
                        cellContent = (
                          <a 
                            href={href} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {stringValue}
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        );
                      } else if (cell.column.columnDef.cell) {
                        // Renderer personalizzato (lookup, boolean, etc.)
                        const rendered = cell.column.columnDef.cell(cell.getContext());
                        // Se il risultato è un elemento React (bottone, etc.), non troncare
                        if (React.isValidElement(rendered)) {
                          shouldTruncate = false;
                          cellContent = rendered;
                        } else {
                          // Se è una stringa dal lookup, applica troncamento e strip HTML
                          cellContent = stripHtmlTags(String(rendered || ''));
                        }
                      } else {
                        // Valore normale (già strippato sopra)
                        cellContent = stringValue;
                      }
                    } else {
                      cellContent = '';
                      shouldTruncate = false;
                    }
                    
                    return (
                      <td
                        key={cell.id}
                        className="px-3 py-2 text-sm border-r border-gray-200 dark:border-gray-700 last:border-r-0"
                        style={cell.column.columnDef.meta?.maxWidth ? { maxWidth: cell.column.columnDef.meta.maxWidth, overflow: 'hidden' } : undefined}
                      >
                        {shouldTruncate && typeof cellContent === 'string' ? (
                          <TruncatedCell content={cellContent} maxLines={2} />
                        ) : (
                          cellContent
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};